import { useContext } from "react";
import { OrderIdContext, StageContext } from "../contexes/OrderIdContext";
import { useMarketplaceId } from "../contexes/MarketplaceIdContext";
import { getCountryCodeFromMarketplace } from "../helpers/marketplaceUtils";
import { useHealthStatus } from "./useHealthStatus";
import { DlqInfo } from "./useFetchDlqInfo";
import { useHealthState } from "../contexes/HealthStateContext";
import { isFreeTrial } from "../helpers/freeTrialUtils";
import { CanonicalBusinessEvent } from "../types/cbes";
import {
  deriveOrderDetails,
  getGlMapping,
  SummaryEvent,
  SummaryOrderDetails,
} from "../helpers/summaryDataUtils";

export { GL_MAPPING, getGlMapping } from "../helpers/summaryDataUtils";

export function useSummaryData(
  eventData: SummaryEvent[],
  dlqInfo: DlqInfo,
  cbeData?: CanonicalBusinessEvent[],
  orderDetailsOverride?: SummaryOrderDetails
) {
  const stage = useContext(StageContext);
  const orderId = useContext(OrderIdContext);
  const { marketplaceId } = useMarketplaceId();
  const countryCode = getCountryCodeFromMarketplace(marketplaceId);

  const { healthSteps, flowSteps } = useHealthStatus(dlqInfo.status);

  const healthStateContext = useHealthState();
  const healthStatus = healthStateContext.overallHealth;

  // Show loading spinner when health status is unknown (initial state) or DLQ info is still loading
  const isHealthLoading = healthStatus === "unknown";

  const freeTrialStatus = isFreeTrial(eventData);

  const paymentFlow = cbeData?.find(
    (cbe) => cbe.internalAttributes?.paymentFlow
  )?.internalAttributes?.paymentFlow;

  const paymentMethodClass = cbeData?.find(
    (cbe) => cbe.internalAttributes?.paymentMethodClass
  )?.internalAttributes?.paymentMethodClass;

  const orderDetails =
    orderDetailsOverride ?? deriveOrderDetails(eventData, cbeData);

  return {
    stage,
    orderId,
    countryCode: orderDetails.countryCodeOverride || countryCode,
    healthStatus,
    healthSteps,
    orderDate: orderDetails.orderDate,
    glProductGroup: orderDetails.glProductGroup,
    consumptionType: orderDetails.consumptionType,
    fulfillmentType: orderDetails.fulfillmentType,
    paymentFlow,
    paymentMethodClass,
    paymentMethod: orderDetails.paymentMethod,
    isFreeTrial: freeTrialStatus,
    isBundleImmediate: orderDetails.isBundleImmediate,
    isSeasonPassBundle: orderDetails.isSeasonPassBundle,
    isPreorderBundle: orderDetails.isPreorderBundle,
    getGlMapping,
    lastItem: orderDetails.lastItem,
    isHealthLoading,
    flowSteps,
    isMgm: orderDetails.isMgm,
    declaredCountryCode: orderDetails.declaredCountryCode,
  };
}
